import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import './standalone-calendar.css'

/**
 * StandaloneCalendar Component
 * Features:
 * 1. Disables dates older than today (configurable)
 * 2. Supports single date or date range selection
 * 3. Shows a label with the date number
 * 4. Allows disabling of specific dates via `unavailableDates` prop
 * 5. Supports min/max day validation for date ranges
 *
 * Props:
 * - mode: 'single' | 'range' (default: 'single')
 * - labelForDate: function(date: string) => string  (label to show under date number)
 * - unavailableDates: array of date strings (YYYY-MM-DD) to disable
 * - onChange: function({from, to}) for range, or function(date) for single
 * - onMonthChange: function(month: moment) => void (callback when month changes)
 * - minDays: number (minimum days for range selection, default: 1)
 * - maxDays: number (maximum days for range selection, default: null)
 * - onRangeError: function(errorType, message) => void (callback for range validation errors)
 * - initialSelectedDate: string (YYYY-MM-DD) for pre-selecting a date in single mode
 * - initialSelectedDateRange: object {from: string, to: string} for pre-selecting a date range in range mode
 * - persistSelectedDate: boolean (if true, selected date persists across month changes)
 * - disablePastDates: boolean (if true, dates before today are disabled, default: true)
 */
const StandaloneCalendar = ({
  mode = 'single',
  labelsForDates = {},
  unavailableDates = [],
  onChange,
  onMonthChange,
  onRangeError,
  initialMonth,
  initialSelectedDate = null,
  initialSelectedDateRange = null,
  persistSelectedDate = false,
  disablePastDates = true,
  minDays = 1,
  maxDays = null,
}) => {
  const today = moment().startOf('day')

  // Initialize month based on initialSelectedDateRange or initialSelectedDate or current month
  const getInitialMonth = () => {
    if (initialMonth) return moment(initialMonth)
    if (mode === 'range' && initialSelectedDateRange?.from) return moment(initialSelectedDateRange.from)
    if (initialSelectedDate) return moment(initialSelectedDate)
    return moment()
  }

  // Initialize range state based on initialSelectedDateRange
  const getInitialRange = () => {
    if (mode === 'range' && initialSelectedDateRange?.from && initialSelectedDateRange?.to) {
      return {
        from: moment(initialSelectedDateRange.from),
        to: moment(initialSelectedDateRange.to),
      }
    }
    return { from: null, to: null }
  }

  const [month, setMonth] = useState(getInitialMonth())
  const [selected, setSelected] = useState(initialSelectedDate) // for single select
  const [range, setRange] = useState(getInitialRange()) // for range select
  const prevInitialSelectedDate = useRef(initialSelectedDate)
  const prevInitialSelectedDateRange = useRef(initialSelectedDateRange)
  const isInitializedRef = useRef(false)

  // Watch for changes to initialSelectedDate prop and update internal state
  useEffect(() => {
    // Only process initialSelectedDate changes if the actual value has changed
    if (prevInitialSelectedDate.current !== initialSelectedDate) {
      prevInitialSelectedDate.current = initialSelectedDate

      if (initialSelectedDate) {
        setSelected(initialSelectedDate)

        // If initialSelectedDate is provided and it's in a different month, update the month view
        if (moment(initialSelectedDate).isValid()) {
          const newMonth = moment(initialSelectedDate)
          setMonth(newMonth)
        }
      } else {
        // If initialSelectedDate becomes null/undefined, clear the selection
        setSelected(null)
      }
    }
  }, [initialSelectedDate])

  // Watch for changes to initialSelectedDateRange prop and update internal state
  useEffect(() => {
    if (mode === 'range') {
      // Only process initialSelectedDateRange changes if:
      // 1. Component hasn't been initialized yet, OR
      // 2. The actual values have changed (deep comparison), not just the object reference
      const hasActuallyChanged =
        prevInitialSelectedDateRange.current?.from !== initialSelectedDateRange?.from ||
        prevInitialSelectedDateRange.current?.to !== initialSelectedDateRange?.to

      if (!isInitializedRef.current || hasActuallyChanged) {
        prevInitialSelectedDateRange.current = initialSelectedDateRange
        isInitializedRef.current = true

        if (initialSelectedDateRange?.from && initialSelectedDateRange?.to) {
          const fromMoment = moment(initialSelectedDateRange.from)
          const toMoment = moment(initialSelectedDateRange.to)

          if (fromMoment.isValid() && toMoment.isValid()) {
            setRange({
              from: fromMoment,
              to: toMoment,
            })

            // Update month view to show the 'from' date
            setMonth(fromMoment.clone())
          }
        } else if (initialSelectedDateRange === null) {
          // If initialSelectedDateRange becomes null, clear the range selection
          setRange({ from: null, to: null })
        }
      }
    }
  }, [initialSelectedDateRange, mode])

  const daysInMonth = month.daysInMonth()
  const firstDayOfWeek = month.clone().date(1).day()

  const isUnavailable = (date) => {
    const dateStr = date.format('YYYY-MM-DD')
    return unavailableDates.includes(dateStr)
  }

  const isPast = (date) => {
    if (disablePastDates) {
      return date.isBefore(today, 'day')
    }
    return false
  }

  const validateDateRange = (startDate, endDate) => {
    const daysDiff = endDate.diff(startDate, 'days') + 1 // +1 to include both start and end days

    if (daysDiff < minDays) {
      return {
        valid: false,
        error: 'MIN_DAYS',
        message: `Minimum ${minDays} day${minDays > 1 ? 's' : ''} required for booking`,
      }
    }

    if (maxDays && daysDiff > maxDays) {
      return {
        valid: false,
        error: 'MAX_DAYS',
        message: `Maximum ${maxDays} day${maxDays > 1 ? 's' : ''} allowed for booking`,
      }
    }

    return { valid: true }
  }

  const checkUnavailableDatesBetween = (startDate, currentMonth) => {
    // Check if there are any unavailable dates between start date and current month
    if (!startDate) return false

    const monthStart = currentMonth.clone().startOf('month')
    const monthEnd = currentMonth.clone().endOf('month')

    // Determine the range to check
    let checkStart, checkEnd
    if (startDate.isBefore(monthStart)) {
      checkStart = startDate.clone().add(1, 'day')
      checkEnd = monthStart.clone().subtract(1, 'day')
    } else if (startDate.isAfter(monthEnd)) {
      checkStart = monthEnd.clone().add(1, 'day')
      checkEnd = startDate.clone().subtract(1, 'day')
    } else {
      // Start date is in current month, no need to check
      return false
    }

    // Check each day in the range
    let cursor = checkStart.clone()
    while (cursor.isSameOrBefore(checkEnd, 'day')) {
      if (isPast(cursor) || isUnavailable(cursor)) {
        return true
      }
      cursor.add(1, 'day')
    }

    return false
  }

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth)
    onMonthChange && onMonthChange(newMonth)

    // If in range mode and there's a start date selected
    if (mode === 'range' && range.from && !range.to) {
      // Check if there are unavailable dates between start date and new month
      if (checkUnavailableDatesBetween(range.from, newMonth)) {
        // Clear the start date selection
        setRange({ from: null, to: null })
        onRangeError &&
          onRangeError('MONTH_CHANGE_RESET', 'Start date cleared due to unavailable dates in between months')
      }
    }

    // If in single mode and persistSelectedDate is false, clear selection when changing months
    // unless the selected date is in the new month
    if (mode === 'single' && !persistSelectedDate && selected) {
      const selectedMoment = moment(selected)
      if (!selectedMoment.isSame(newMonth, 'month')) {
        setSelected(null)
        onChange && onChange(null)
      }
    }
  }

  const handleDateClick = (date) => {
    if (isPast(date) || isUnavailable(date)) return

    if (mode === 'single') {
      setSelected(date.format('YYYY-MM-DD'))
      onChange && onChange(date.format('YYYY-MM-DD'))
    } else {
      if (!range.from || (range.from && range.to)) {
        // Starting new range selection
        setRange({ from: date, to: null })
      } else {
        // Completing range selection
        const start = date.isBefore(range.from, 'day') ? date : range.from
        const end = date.isAfter(range.from, 'day') ? date : range.from

        // Validate min/max days
        const validation = validateDateRange(start, end)
        if (!validation.valid) {
          onRangeError && onRangeError(validation.error, validation.message)
          // Reset range selection
          setRange({ from: null, to: null })
          return
        }

        // Check if all dates in range are available
        let allAvailable = true
        let cursor = start.clone()
        while (cursor.isSameOrBefore(end, 'day')) {
          if (isPast(cursor) || isUnavailable(cursor)) {
            allAvailable = false
            break
          }
          cursor.add(1, 'day')
        }

        if (allAvailable) {
          setRange({ from: start, to: end })
          onChange &&
            onChange({
              from: start.format('YYYY-MM-DD'),
              to: end.format('YYYY-MM-DD'),
              days: end.diff(start, 'days') + 1,
            })
        } else {
          onRangeError && onRangeError('UNAVAILABLE_DATES', 'Selected range contains unavailable dates')
          // Reset range selection
          setRange({ from: null, to: null })
        }
      }
    }
  }

  const isSelected = (date) => {
    if (mode === 'single') {
      return selected === date.format('YYYY-MM-DD')
    } else if (range.from && range.to) {
      return date.isSameOrAfter(range.from, 'day') && date.isSameOrBefore(range.to, 'day')
    } else if (range.from) {
      return date.isSame(range.from, 'day')
    }
    return false
  }

  const isInValidRange = (date) => {
    // Helper to show visual feedback for valid range while selecting
    if (mode !== 'range' || !range.from || range.to) return false

    const start = date.isBefore(range.from, 'day') ? date : range.from
    const end = date.isAfter(range.from, 'day') ? date : range.from

    const validation = validateDateRange(start, end)
    return validation.valid
  }

  return (
    <div className="standalone-calendar">
      <div className="calendar-header">
        <button
          onClick={() => {
            const newMonth = month.clone().subtract(1, 'month')
            handleMonthChange(newMonth)
          }}
          disabled={disablePastDates && month.isSameOrBefore(today, 'month')}
        >
          {'<'}
        </button>
        <span
          style={{
            color: '#15bcc3',
            fontSize: '1.2rem',
            fontWeight: 'bold',
          }}
        >
          {month.format('MMMM YYYY')}
        </span>
        <button
          onClick={() => {
            const newMonth = month.clone().add(1, 'month')
            handleMonthChange(newMonth)
          }}
        >
          {'>'}
        </button>
      </div>

      {/* Range selection info */}
      {mode === 'range' && (minDays > 1 || maxDays) && (
        <div
          className="range-info"
          style={{
            textAlign: 'center',
            fontSize: '0.85rem',
            color: '#666',
            marginBottom: '10px',
            padding: '5px',
          }}
        >
          {minDays > 1 && maxDays
            ? `Select ${minDays}-${maxDays} days`
            : minDays > 1
              ? `Minimum ${minDays} days required`
              : maxDays
                ? `Maximum ${maxDays} days allowed`
                : null}
        </div>
      )}

      {/* Selected date info for single mode with persistent selection */}
      {mode === 'single' && persistSelectedDate && selected && !moment(selected).isSame(month, 'month') && (
        <div
          className="selected-date-info"
          style={{
            textAlign: 'center',
            fontSize: '0.85rem',
            color: '#1976d2',
            marginBottom: '10px',
            padding: '8px',
            background: '#e3f2fd',
            border: '1px solid #bbdefb',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
          }}
        >
          <span>Selected: {moment(selected).format('MMM DD, YYYY')}</span>
          <button
            style={{
              background: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '2px 8px',
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
            onClick={() => {
              const selectedMonth = moment(selected)
              handleMonthChange(selectedMonth)
            }}
          >
            Go to Date
          </button>
        </div>
      )}

      <div className="calendar-weekdays">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="calendar-days">
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <span
            key={`empty-${i}`}
            className="empty"
          ></span>
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const date = month.clone().date(i + 1)
          const disabled = isPast(date) || isUnavailable(date)
          const validRange = isInValidRange(date)

          return (
            <span
              key={date.format('YYYY-MM-DD')}
              className={`calendar-day${disabled ? ' disabled' : ''}${
                isSelected(date) ? ' selected' : ''
              }${validRange ? ' valid-range' : ''}`}
              onClick={() => handleDateClick(date)}
            >
              <div className="date-number">{i + 1}</div>
              {/* Render multiple labels if provided */}
              {labelsForDates && !disabled && labelsForDates[date.format('YYYY-MM-DD')] && (
                <div className="date-labels">
                  {labelsForDates[date.format('YYYY-MM-DD')].map((lbl, idx) => (
                    <div
                      className="date-label badge"
                      key={idx}
                    >
                      {lbl}
                    </div>
                  ))}
                </div>
              )}
            </span>
          )
        })}
      </div>

      {/* Current selection display */}
      {mode === 'range' && range.from && (
        <div
          className="selection-info"
          style={{
            textAlign: 'center',
            marginTop: '10px',
            fontSize: '0.9rem',
            color: '#333',
          }}
        >
          {range.to ? (
            <span>
              Selected: {range.from.format('MMM DD')} - {range.to.format('MMM DD')}(
              {range.to.diff(range.from, 'days') + 1} day{range.to.diff(range.from, 'days') + 1 > 1 ? 's' : ''})
            </span>
          ) : (
            <span>Start: {range.from.format('MMM DD')} (Select end date)</span>
          )}
        </div>
      )}
    </div>
  )
}

export default StandaloneCalendar
